import type { SortingState } from '@tanstack/react-table';

export type RegulatoryWatchSessionPageId =
  | 'all-documents'
  | 'newsfeed'
  | 'audit-table'
  | 'all-updates';

export type AllDocumentsPersistedSort = {
  column: string;
  direction: 'asc' | 'desc';
} | null;

export type AllDocumentsPersistedWidths = Partial<{
  lastUpdated: number;
  type: number;
  region: number;
  source: number;
  action: number;
}>;

export type RegulatoryWatchPageSessionV1 = {
  v: 1;
  dirty: boolean;
  /** Serialized query string without leading `?` */
  search: string;
  scrollY?: number;
  pagination?: { pageIndex: number; pageSize: number };
  sorting?: SortingState;
  tableSort?: AllDocumentsPersistedSort;
  columnWidths?: AllDocumentsPersistedWidths;
  openGroupKeys?: string[];
};

const STORAGE_PREFIX = 'ditto:rw-page-session:v1:';

export function rwPageSessionStorageKey(
  workspaceUname: string,
  pageId: RegulatoryWatchSessionPageId
): string {
  return `${STORAGE_PREFIX}${workspaceUname}:${pageId}`;
}

export function loadRwPageSession(
  workspaceUname: string,
  pageId: RegulatoryWatchSessionPageId
): RegulatoryWatchPageSessionV1 | null {
  try {
    const raw = sessionStorage.getItem(
      rwPageSessionStorageKey(workspaceUname, pageId)
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RegulatoryWatchPageSessionV1;
    if (parsed?.v !== 1 || typeof parsed.search !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function mergeRwPageSession(
  workspaceUname: string,
  pageId: RegulatoryWatchSessionPageId,
  patch: Partial<Omit<RegulatoryWatchPageSessionV1, 'v'>>
): void {
  try {
    const prev = loadRwPageSession(workspaceUname, pageId);
    const next: RegulatoryWatchPageSessionV1 = {
      v: 1,
      dirty: false,
      search: '',
      ...(prev ?? {}),
      ...patch,
    };
    sessionStorage.setItem(
      rwPageSessionStorageKey(workspaceUname, pageId),
      JSON.stringify(next)
    );
  } catch {
    // ignore quota / private mode
  }
}
